import { fmtAge, relayAge, relayAgeText, relayEpochAge, shortKey, type RelayAge } from './healthFormat';
import { Stat } from './healthPrimitives';
import type {
  FillsPollerInfo,
  G1HealthSnapshot,
  OrgCacheHealth,
  OrgWsHealth,
  QueueFetchLanes,
  ShadowHealth,
  SweepHealth,
} from './types';

/** The sweep tile's sub line. A null stamp keeps its long-standing "no run yet";
 *  the credited/fail counts belong to the last run and still show beside a stamp
 *  the page could not read, since only the time is in doubt. */
function sweepSub(sweep: SweepHealth, serverTs: number): string {
  const age = relayAge(serverTs, sweep.lastRunAt);
  if (age.kind === 'missing') return 'no run yet';
  const fails = sweep.consecutiveFailures > 0 ? ` · ${sweep.consecutiveFailures} fails` : '';
  return `${relayAgeText(age, 'last run')} · ${sweep.lastSettlementsCredited} credited${fails}`;
}

type PollerRow = OrgCacheHealth['orgs'][number];

/**
 * Queue freshness for one org, from the relay's own counts. Deliberately no colour
 * on these: a large book is `retained` and below full between chunks as a matter
 * of course, so tinting either would announce outages that are not happening. The
 * relay's failure counter (`Q fails`) is what carries the alarm, as `Fails` does
 * for the orders poll.
 */
function queueFreshText(p: PollerRow): string {
  // An org with no resting orders makes no queue request at all — nothing is late.
  if (p.orderCount === 0 && p.hasSuccessfulSnapshot !== false) return 'no orders';
  return p.queueFreshOrderCount === undefined ? '—' : `${p.queueFreshOrderCount}/${p.orderCount}`;
}
function queueHeldText(p: PollerRow): string {
  if (p.queueCount === undefined) return '—';
  return p.queueRetained ? `${p.queueCount} retained` : String(p.queueCount);
}

/** "last run 1m01 ago" — the event named before an age, and inside the phrase
 *  when the time could not be used. */
const eventAge = (age: Exclude<RelayAge, { kind: 'missing' }>, event: string) =>
  age.kind === 'age' ? `${event} ${relayAgeText(age, event)}` : relayAgeText(age, event);

/** The shadow tile's sub line. Its headline number is a running total of field
 *  differences observed since the relay started — a persistent one is recounted on
 *  every verifier pass — so the line says that first, then WHEN one was last seen,
 *  which is the part that speaks to now. `usersChecked` is likewise a tally of
 *  checks, not a count of accounts. */
function shadowSub(shadow: ShadowHealth, mode: string | undefined, serverTs: number): string {
  const parts = ['differences since relay start'];
  const seen = relayAge(serverTs, shadow.lastDivergenceAt);
  if (seen.kind !== 'missing') parts.push(eventAge(seen, 'last seen'));
  parts.push(`${shadow.usersChecked} checks`);
  const run = relayAge(serverTs, shadow.lastRunAt);
  parts.push(run.kind === 'missing' ? 'no run yet' : eventAge(run, 'last run'));
  parts.push(`mode ${mode ?? '?'}`);
  return parts.join(' · ');
}

/** The REST fills tooltip's poll age: an epoch number from the same relay process,
 *  held to the same rule — a future stamp is said in words, never clamped to 0s. */
function fillsPollText(fills: FillsPollerInfo, serverTs: number): string {
  const age = relayEpochAge(serverTs, fills.lastPollAt);
  return age.kind === 'missing' ? 'last poll never' : eventAge(age, 'last poll');
}

/** ONE predicate drives the lane tile's tone and its words. `oldestOverdueMs` turns
 *  positive the moment a lane passes its deadline, while `timedOutAwaitingSettlement`
 *  only counts once the deadline timer has run — so under event-loop lag the first
 *  can be set without the second, and a tile keyed on either alone would go amber
 *  while saying "none past deadline". */
const lanesPastDeadline = (lanes: QueueFetchLanes) => lanes.timedOutAwaitingSettlement > 0 || lanes.oldestOverdueMs > 0;

function lanesSub(lanes: QueueFetchLanes): string {
  const deadline = lanesPastDeadline(lanes)
    ? `oldest ${fmtAge(lanes.oldestOverdueMs)} past deadline${
        lanes.timedOutAwaitingSettlement > 0 ? ` · ${lanes.timedOutAwaitingSettlement} awaiting settlement` : ''
      }`
    : 'none past deadline';
  return `relay-wide · in flight · ${deadline} · ${lanes.forceReleased} force-released since relay start`;
}

// Feed liveness — the "is the pipeline alive end to end?" board. Absorbs the
// old Org-broker-cache table and joins it with the WS fills sockets and the
// REST fills backstop into ONE row per org, under a tile row for the push
// channel / settlement sweeper / shadow verifier / relay-wide queue requests.
// This is the real-time answer to "are the feeds live?" without ssh-ing into a box.
export function FeedLiveness({
  snap,
  orgNames,
  inSel,
}: {
  snap: G1HealthSnapshot;
  orgNames: Record<string, string>;
  inSel: (orgId: string | null) => boolean;
}) {
  const push = snap.push;
  const sweep = snap.settlementSweep;
  const shadow = snap.positionStates?.shadow;
  const mode = snap.positionStates?.mode;

  // One row per org: union of the three per-org health sources.
  const byOrg = new Map<
    string,
    {
      poller?: OrgCacheHealth['orgs'][number];
      ws?: OrgWsHealth;
      fills?: FillsPollerInfo;
    }
  >();
  const rowFor = (orgId: string) => {
    const r = byOrg.get(orgId) ?? {};
    byOrg.set(orgId, r);
    return r;
  };
  for (const o of snap.orgCache.orgs) if (inSel(o.orgId)) rowFor(o.orgId).poller = o;
  for (const w of snap.kalshiWs ?? []) if (inSel(w.orgId)) rowFor(w.orgId).ws = w;
  for (const f of snap.fillsPoller ?? []) if (inSel(f.orgId)) rowFor(f.orgId).fills = f;
  const rows = [...byOrg.entries()].sort((a, b) => {
    const an = orgNames[a[0]] ?? a[0];
    const bn = orgNames[b[0]] ?? b[0];
    return an.localeCompare(bn);
  });

  const suppressionPct =
    push && push.sent + push.suppressed > 0
      ? Math.round((push.suppressed / (push.sent + push.suppressed)) * 100)
      : null;
  const pollMs = snap.orgPollMs;
  // Relay-wide: one aggregate for the whole process, so it sits with the tiles and
  // is never narrowed by the org drill-down.
  const lanes = snap.queueFetchLanes;

  return (
    <section className="border-border rounded-lg border p-4">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-foreground text-sm font-semibold">Feed liveness</h2>
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
            snap.orgCache.polling ? 'bg-green-600/80 text-white' : 'bg-muted text-muted-foreground'
          }`}
        >
          {snap.orgCache.polling ? 'POLLING' : 'IDLE'}
        </span>
        <span className="text-muted-foreground text-[11px]">
          per org: shared {pollMs != null ? `${pollMs}ms` : ''} order/queue poller · WS fills socket · REST fills
          backstop
        </span>
      </div>

      {/* Pipeline tile row — the fan-out side (relay → browsers) + background loops. */}
      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Stat
          label="Push channel"
          value={push ? `${push.connections}` : '—'}
          sub={push ? `conns · ${push.users} users` : 'not reported'}
        />
        <Stat
          label="Push suppression"
          value={suppressionPct != null ? `${suppressionPct}%` : '—'}
          sub={
            push
              ? `${push.sent.toLocaleString()} sent · ${push.suppressed.toLocaleString()} suppressed since relay start`
              : undefined
          }
        />
        <Stat
          label="Resolver errors"
          value={push ? String(push.resolverErrors) : '—'}
          tone={push && push.resolverErrors > 0 ? 'danger' : 'normal'}
          sub="push topic resolves failed since relay start"
        />
        <Stat
          label="Settlement sweep"
          value={sweep ? (sweep.running ? 'on' : 'off') : '—'}
          tone={sweep && sweep.consecutiveFailures > 0 ? 'warn' : 'normal'}
          sub={sweep ? sweepSub(sweep, snap.serverTs) : undefined}
        />
        <Stat
          label="Shadow parity"
          value={shadow ? String(shadow.divergences) : '—'}
          tone={shadow && shadow.divergences > 0 ? 'danger' : 'normal'}
          sub={shadow ? shadowSub(shadow, mode, snap.serverTs) : undefined}
        />
        <Stat
          label="Queue requests"
          value={lanes ? String(lanes.inFlight) : '—'}
          tone={lanes && lanesPastDeadline(lanes) ? 'warn' : 'normal'}
          sub={lanes ? lanesSub(lanes) : undefined}
        />
      </div>

      {rows.length === 0 ? (
        <p className="text-muted-foreground py-3 text-center text-xs">No active orgs right now.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead className="text-muted-foreground border-border border-b">
              <tr>
                <th className="py-1.5 pr-3 font-medium">Org</th>
                <th className="px-3 text-right font-medium">Orders cached</th>
                <th className="px-3 text-center font-medium" title="order/queue poller snapshot state">Poller</th>
                <th className="px-3 text-right font-medium" title="poller snapshot age — should hover near the poll cadence">Age</th>
                <th className="px-3 text-right font-medium" title="consecutive failed polls">Fails</th>
                <th className="px-3 text-right font-medium" title="empty-overwrite guards tripped while this org has been polled — resets when the org goes idle">Empty-guards</th>
                <th
                  className="px-3 text-right font-medium"
                  title="resting orders with a queue observation inside the relay's freshness window / orders cached. A large book dips below full between chunks; no orders = no queue request is needed"
                >
                  Queue fresh
                </th>
                <th
                  className="px-3 text-right font-medium"
                  title="rows in the relay's queue map, INCLUDING values kept from earlier reads — an inventory, not fresh data. retained = the relay is serving held values instead of one complete fresh read"
                >
                  Queue held
                </th>
                <th
                  className="px-3 text-right font-medium"
                  title="age of the queue reading the relay is still using. Unlike Age, an orders poll does not refresh it"
                >
                  Queue age
                </th>
                <th className="px-3 text-right font-medium" title="consecutive failed queue refreshes">
                  Q fails
                </th>
                <th className="px-3 text-center font-medium" title="Kalshi user-fills WebSocket">WS fills</th>
                <th className="px-3 text-right font-medium" title="WS: reconnects · sequence gaps over this socket's lifetime">rc/gaps</th>
                <th className="px-3 text-right font-medium" title="fills ingested over this socket's lifetime">WS fills #</th>
                <th className="px-3 text-center font-medium" title="REST fills poller: primary (WS down/off) or backstop (WS healthy)">REST fills</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([orgId, r]) => {
                const stale = r.poller && !r.poller.ok;
                const wsUp = r.ws?.connected && r.ws?.subscribed;
                return (
                  <tr key={orgId} className={`border-border/50 border-b ${stale ? 'bg-red-500/10' : ''}`}>
                    <td className="text-foreground py-1.5 pr-3">{orgNames[orgId] ?? shortKey(`org:${orgId}`).id}</td>
                    <td className="text-foreground px-3 text-right">{r.poller?.orderCount ?? '—'}</td>
                    <td className="px-3 text-center">
                      {r.poller ? (
                        r.poller.ok ? (
                          <span className="text-green-400">ok</span>
                        ) : (
                          <span className="font-bold text-red-400">STALE</span>
                        )
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="text-muted-foreground px-3 text-right">
                      {r.poller ? fmtAge(r.poller.ageMs) : '—'}
                    </td>
                    <td className={`px-3 text-right ${r.poller && r.poller.consecutiveFailures > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                      {r.poller?.consecutiveFailures ?? '—'}
                    </td>
                    <td className={`px-3 text-right ${r.poller && r.poller.suspectedEmptyOverwrites > 0 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                      {r.poller?.suspectedEmptyOverwrites ?? '—'}
                    </td>
                    <td className="text-muted-foreground px-3 text-right">{r.poller ? queueFreshText(r.poller) : '—'}</td>
                    <td className="text-muted-foreground px-3 text-right">{r.poller ? queueHeldText(r.poller) : '—'}</td>
                    <td className="text-muted-foreground px-3 text-right">
                      {r.poller?.queueAgeMs != null ? fmtAge(r.poller.queueAgeMs) : '—'}
                    </td>
                    <td
                      className={`px-3 text-right ${r.poller?.queueConsecutiveFailures ? 'text-red-400' : 'text-muted-foreground'}`}
                    >
                      {r.poller?.queueConsecutiveFailures ?? '—'}
                    </td>
                    <td className="px-3 text-center">
                      {r.ws ? (
                        wsUp ? (
                          <span
                            className="text-green-400"
                            title={`key ${r.ws.keyLabel ?? '?'} · last event ${r.ws.lastEventAgeMs != null ? fmtAge(r.ws.lastEventAgeMs) : '—'} ago`}
                          >
                            LIVE
                          </span>
                        ) : (
                          <span className="font-bold text-red-400" title="socket down or not subscribed — REST poller should be primary">
                            DOWN
                          </span>
                        )
                      ) : (
                        <span className="text-muted-foreground" title="KALSHI_WS_FILLS off or no socket for this org">—</span>
                      )}
                    </td>
                    <td className={`px-3 text-right ${r.ws && (r.ws.seqGaps > 0 || r.ws.reconnects > 0) ? 'text-amber-400' : 'text-muted-foreground'}`}>
                      {r.ws ? `${r.ws.reconnects}/${r.ws.seqGaps}` : '—'}
                    </td>
                    <td className="text-muted-foreground px-3 text-right">{r.ws?.fillsIngested ?? '—'}</td>
                    <td className="px-3 text-center">
                      {r.fills ? (
                        <span
                          className={r.fills.mode === 'backstop' ? 'text-green-400' : 'text-amber-400'}
                          title={`${fillsPollText(r.fills, snap.serverTs)} · last inserted ${r.fills.lastInserted}. backstop = WS is primary (good); primary = REST carrying fills (WS down/off).`}
                        >
                          {r.fills.mode}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-muted-foreground mt-2 text-[11px]">
        Healthy shape: poller <span className="text-foreground">ok</span> with age ≈ the poll cadence, queue fresh near
        its orders with Q fails 0 and no queue requests past deadline, WS{' '}
        <span className="text-foreground">LIVE</span> with rc/gaps 0/0, REST fills{' '}
        <span className="text-foreground">backstop</span>, suppression high (quiet books send nothing), resolver
        errors 0, no shadow differences since relay start.
      </p>
    </section>
  );
}
